import { DocumentPlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import React, { useId, useRef } from 'react';
import { FormProvider, useWatch, type Resolver } from 'react-hook-form';
import { ActorRefFrom } from 'xstate';
import { useConfirmationView } from '~/app';
import {
  Button,
  Checkbox,
  FormActions,
  FormFeedback,
  FormHelper,
  FormLayout,
  FormRow,
  FormSection,
  Select,
  TextArea,
  TextInput,
} from '~/components';
import { useCustomForm } from '../../../hooks/useCustomForm';
import {
  DOCUMENTS_PICKER_ACCEPT,
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORY_PARSE_HINTS,
  DOCUMENT_TYPES,
  DOCUMENT_TYPE_PARSE_HINTS,
  toTypesLabel,
} from './constants';
import { documentsMachine } from './documents.machine';
import { FileCategorySelect, FileTypeSelect, type FileTypeOption } from './FileTypeSelect';
import { withParseHint } from './parseHintLabel';
import { INSURANCE_CERTIFICATE_UPLOAD } from './parseWorkflow';
import { DocumentInfoSchema, DocumentInfoValues, MultiFileInfoSchema } from './types';
import { getCombineBlocker, withPdfExtension } from './uploadBatch';
import {
  documentsUploadExtensions,
  getFilingIssue,
  getInlineRejectionText,
  getRejectionAlert,
  getRowParseNotice,
  getUploadRejection,
  splitUploadable,
  trimFileName,
  type UploadRejection,
} from './utils';

const TAGS = CT.documents;

// If the user renames the doc and drops the extension (e.g. `report.pdf` →
// `Q3 Report`), preserve the original extension on save. A rename is a UX
// concern, not a file-format change — stripping ".pdf" shouldn't silently
// turn a PDF row into an extensionless blob.
const preserveExtension = (newName: string, originalName: string | undefined): string => {
  if (!originalName) return newName;
  const origMatch = /\.([^./\\]+)$/.exec(originalName);
  const origExt = origMatch?.[1];
  if (!origExt) return newName;
  const alreadyHasExt = /\.[^./\\]+$/.test(newName);
  return alreadyHasExt ? newName : `${newName}.${origExt}`;
};

// Picking the same file twice must not list it twice.
const fileKey = (file: File) => `${file.name}:${file.size}:${file.lastModified}`;

// [MCV-1765] Every picked file becomes its own document (or, combined, one packet) and carries
// its own File Type. [MCV-1810] That row type is the ONLY File Type in add mode, for one file as
// for several (the form's shared File Type field exists only when editing an existing document),
// and it is required: a row without one blocks Save.
// [MCV-1848] The File Category is per row too, to the right of the File Type, and optional.
// `categoryDefaulted` marks a category this code filled in (a Certificate's Malpractice Insurance),
// so it can be taken back; one the user picked by hand is never touched.
interface StagedFile {
  file: File;
  docType: FileTypeOption | null;
  docCategory: FileTypeOption | null;
  categoryDefaulted?: boolean;
}

const singleResolver = zodResolver(DocumentInfoSchema) as unknown as Resolver<DocumentInfoValues>;
const multiResolver = zodResolver(MultiFileInfoSchema) as unknown as Resolver<DocumentInfoValues>;

const PACKET_TYPE = DOCUMENT_TYPES.find(({ value }) => value === 'packet') as FileTypeOption;
const MALPRACTICE_CATEGORY = DOCUMENT_CATEGORIES.find(
  ({ value }) => value === INSURANCE_CERTIFICATE_UPLOAD.docCategory
) as FileTypeOption;

const MISSING_TYPE_TEXT = 'File Type is required';

// [MCV-1306] Shown before an edit that changes the File Type is saved.
const TYPE_CHANGE_MESSAGE =
  'Changing the document type may affect how previous parse results are interpreted. Continue?';

interface Props {
  actor: ActorRefFrom<typeof documentsMachine>;
}

export const DocumentInfoForm = ({ actor }: Props) => {
  const { form, isUploading, uploads } = useSelector(actor, (state) => state.context);
  const confirmationRef = useConfirmationView();
  // Add mode: a fresh form with no persisted document yet (id < 0). The user
  // picks a file inside the modal, then Save validates metadata and uploads
  // the file with that metadata in one request.
  const isAdd = !form.id || form.id < 0;
  const [stagedFiles, setStagedFiles] = React.useState<StagedFile[]>([]);
  // [MCV-1810] Only the File Name input and its "saved under its own name" helper depend on the
  // count; the File Type is per row whatever it is.
  const multipleFiles = isAdd && stagedFiles.length > 1;
  const methods = useCustomForm<DocumentInfoValues>({
    // [MCV-1810] Add mode checks each row's File Type itself (rowIssue), so the form's own
    // File Type field, which add mode does not render, must not be required there.
    resolver: isAdd ? multiResolver : singleResolver,
    defaultValues: form,
  });
  const { handleSubmit, setValue, getValues, control } = methods;
  // The shared File Type and File Category fields exist only when editing: filingIssue reads them.
  const watchedType = useWatch({ control, name: 'docType' });
  const watchedCategory = useWatch({ control, name: 'docCategory' });
  const watchedCombine = useWatch({ control, name: 'combineFiles' });
  // [MCV-1760] [MCV-1810] With no shared File Type, the picker takes every format some File Type
  // accepts (the stored formats plus the parse formats, e.g. an .html or .md CV); rowIssue then
  // holds each file to the formats of ITS type (and category, MCV-1848) once that is chosen.
  const allowedExtensions = documentsUploadExtensions();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // [MCV-1538] Stable id so the dashed box's <label htmlFor> binds to this input.
  const fileInputId = useId();
  const [fileTypeError, setFileTypeError] = React.useState<string | null>(null);
  // Rows missing a File Type say so only after a Save was tried, not while the list is built.
  // [MCV-1810] Kept per file (the files listed at that Save): a file added afterwards starts with
  // no File Type and must not be flagged before the user has had a chance to pick one.
  const [missingTypeChecked, setMissingTypeChecked] = React.useState<ReadonlySet<string>>(
    () => new Set()
  );
  const selectedFiles = stagedFiles.map(({ file }) => file);

  // Remember the ORIGINAL filename (with extension) so we can re-append the
  // extension at submit time if the user removed it during rename. For EDIT
  // this is the doc's current name; for ADD it's the file the user just picked.
  const originalNameRef = useRef<string>(form.docName || '');

  // [MCV-1306] Track original docType so we can warn if changed in edit mode.
  const originalDocTypeRef = useRef<string>(form.docType?.value || '');

  // Attestations are system-generated and their classification links back to
  // the attestation record. Allow renaming (title/description) but lock the
  // type/category so the association can't be stripped off.
  const isAttestation = form.docType?.value?.toLowerCase() === 'attestation';

  // [MCV-1765] "Combine into one packet" is offered for 2+ files that are ALL Packet, and only
  // when the API can merge them all. [MCV-1810] There is no shared File Type to read this from,
  // so it follows the rows.
  const combineBlocker = getCombineBlocker(selectedFiles);
  const offerCombine =
    isAdd &&
    stagedFiles.length >= 2 &&
    stagedFiles.every(({ docType }) => docType?.value === PACKET_TYPE.value);
  const combining = offerCombine && combineBlocker === null && watchedCombine === true;
  // [MCV-1810] A box that is no longer offered (a row left Packet, or the list shrank to one
  // file) must not stay checked: it would apply again when the option comes back.
  React.useEffect(() => {
    if (!offerCombine && getValues('combineFiles') === true) {
      setValue('combineFiles', false, { shouldDirty: false, shouldValidate: false });
    }
  }, [offerCombine, getValues, setValue]);

  // The packet's name starts from the first file's, as a PDF's. [MCV-1810] Every row is already
  // Packet (offerCombine requires it), and the selects lock until the box is unchecked.
  const wasCombining = useRef(false);
  React.useEffect(() => {
    if (combining === wasCombining.current) return;
    wasCombining.current = combining;
    if (combining) {
      setValue('docName', withPdfExtension(getValues('docName')), {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [combining, getValues, setValue]);

  // [MCV-1761] A Certificate parses only under Malpractice Insurance (a PDF), so a Certificate
  // row with no category yet is filed there. [MCV-1848] The category is per row now, so this is
  // per row too: a Certificate beside other files no longer waits for the whole batch. A category
  // the user chose is never overridden; one this code defaulted is taken back when the row stops
  // being a Certificate, so it never lingers on a file it was not meant for.
  const withCertificateDefault = (row: StagedFile): StagedFile => {
    if (row.docType?.value === INSURANCE_CERTIFICATE_UPLOAD.docType) {
      return row.docCategory?.value
        ? row
        : { ...row, docCategory: MALPRACTICE_CATEGORY, categoryDefaulted: true };
    }
    return row.categoryDefaulted ? { ...row, docCategory: null, categoryDefaulted: false } : row;
  };
  // [MCV-1810] Editing an existing document has one File Type and one File Category, in the
  // form's own fields: choosing Certificate files it under Malpractice Insurance when no category
  // is chosen, and moving off Certificate takes back a category defaulted that way.
  const categoryAutoDefaulted = useRef(false);
  const onEditTypeChange = (option: unknown) => {
    const isCertificate = (option as FileTypeOption).value === INSURANCE_CERTIFICATE_UPLOAD.docType;
    const hasCategory = Boolean(getValues('docCategory')?.value);
    if (isCertificate && !hasCategory) {
      categoryAutoDefaulted.current = true;
      setValue('docCategory', MALPRACTICE_CATEGORY, { shouldDirty: true });
    } else if (!isCertificate && categoryAutoDefaulted.current) {
      categoryAutoDefaulted.current = false;
      if (hasCategory) setValue('docCategory', { label: '', value: '' }, { shouldDirty: true });
    }
  };
  const updateRow = (file: File, change: (row: StagedFile) => StagedFile) =>
    setStagedFiles(stagedFiles.map((row) => (row.file === file ? change(row) : row)));
  const setRowType = (file: File, picked: FileTypeOption) =>
    updateRow(file, (row) => withCertificateDefault({ ...row, docType: picked }));
  const setRowCategory = (file: File, picked: FileTypeOption | null) =>
    updateRow(file, (row) => ({ ...row, docCategory: picked, categoryDefaulted: false }));

  // [MCV-1538] No handlePickFile any more: the <label htmlFor> opens the picker
  // natively, and the input's own onClick clears any stale file-type error.
  // The File Name follows the selection: one file pre-fills its own name (trimmed to what the
  // column holds); several are each saved under their own name, so the field only needs a value
  // that passes validation. A combined packet keeps the name the user is typing.
  const applySelection = (rows: StagedFile[]) => {
    const files = rows.map(({ file }) => file);
    setStagedFiles(rows);
    originalNameRef.current = files[0]?.name ?? '';
    const stillCombining =
      getCombineBlocker(files) === null &&
      files.length >= 2 &&
      rows.every(({ docType }) => docType?.value === PACKET_TYPE.value) &&
      getValues('combineFiles') === true;
    if (!stillCombining) {
      setValue('docName', files[0] ? trimFileName(files[0].name) : '', {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
    if (getCombineBlocker(files) !== null || files.length < 2) {
      setValue('combineFiles', false, { shouldDirty: false, shouldValidate: false });
    }
  };
  // [MCV-1785] A rejected file shows the inline message (MCV-1577) AND the same alert popup a
  // rejected drop raises. The files already chosen stay; the rejected ones never join them.
  const rejectFiles = (rejected: UploadRejection[], allowed: readonly string[]) => {
    setFileTypeError(getInlineRejectionText(rejected, allowed));
    actor.send({ type: 'ON_FILES_REJECTED', payload: getRejectionAlert(rejected, allowed) });
  };
  const handleFilesPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileTypeError(null);
    const picked = Array.from(e.target.files ?? []);
    if (picked.length > 0) {
      const { accepted, rejected } = splitUploadable(picked, allowedExtensions);
      if (rejected.length > 0) rejectFiles(rejected, allowedExtensions);
      const known = new Set(selectedFiles.map(fileKey));
      const added = accepted.filter((file) => {
        const key = fileKey(file);
        if (known.has(key)) return false;
        known.add(key);
        return true;
      });
      // [MCV-1810] A new file has no File Type until the user picks one for it, except while the
      // files are being combined: then it joins them as another page of the same packet.
      const startType = combining ? PACKET_TYPE : null;
      if (added.length > 0) {
        applySelection([
          ...stagedFiles,
          ...added.map((file) => ({ file, docType: startType, docCategory: null })),
        ]);
      }
    }
    // Reset so the same file can be re-selected if the user removes it.
    e.target.value = '';
  };
  const removeFile = (file: File) => {
    setFileTypeError(null);
    applySelection(stagedFiles.filter((row) => row.file !== file));
  };

  // [MCV-1848] The category a row files under: its own, none while the files are combined (a
  // combined packet is just a Packet at upload time).
  const rowCategory = (row: StagedFile): string | undefined =>
    combining ? undefined : row.docCategory?.value;

  // What is wrong with one row, or undefined. [MCV-1810] The row's own File Type is the only one,
  // for a lone file too. A row without a type is reported only once `requireType` (after a Save
  // was tried), not while the list is being built.
  const rowIssue = (row: StagedFile, requireType: boolean): string | undefined => {
    const { docType } = row;
    if (!docType?.value) return requireType ? MISSING_TYPE_TEXT : undefined;
    const allowed = documentsUploadExtensions(docType.value, rowCategory(row));
    const rejection = getUploadRejection(row.file, allowed);
    return rejection ? getInlineRejectionText([rejection], allowed) : undefined;
  };
  // [MCV-1802] Editing an existing document: a CV, packet or malpractice certificate must be a
  // format its parse workflow reads, so a File Type / Category the stored file cannot be parsed
  // under is an error on every edit, as for a new upload (the API refuses the same PATCH, 415).
  // [MCV-1802] Both selects point to it and read as invalid, so a screen reader on either one
  // hears why Save is refused (the form resolver does not own this cross-field rule).
  const filingErrorId = `${fileInputId}-filing-error`;
  const filingIssue = isAdd
    ? undefined
    : getFilingIssue(form.uploadedFileName, watchedType?.value, watchedCategory?.value);
  const rowIssues = stagedFiles.map((row) =>
    rowIssue(row, missingTypeChecked.has(fileKey(row.file)))
  );
  // [MCV-1802] What the vault does with each file, said on the file's own row so the user knows
  // before Save: parsed automatically, or uploaded without parsing (getRowParseNotice). [MCV-1810]
  // Every row says it, one file or many; this replaced the line under the selects. Only for a
  // file that passes rowIssue's gate: a file its type cannot take is an error, never a note, and
  // the note changes nothing about what Save accepts. A combined packet says nothing here: the
  // Combine checkbox already says the files are parsed as one packet.
  const rowNotices = stagedFiles.map((row, index) =>
    combining || rowIssues[index] !== undefined
      ? undefined
      : getRowParseNotice(row.docType?.value, rowCategory(row))
  );

  const sendEditSubmit = (values: DocumentInfoValues) =>
    actor.send({
      type: 'ON_SUBMIT_FORM',
      payload: {
        values: {
          ...values,
          docName: preserveExtension(values.docName, originalNameRef.current),
        },
      },
    });

  const onSubmitSuccess = async (values: DocumentInfoValues) => {
    if (isAdd) {
      if (stagedFiles.length === 0) {
        setFileTypeError('Choose a file before saving.');
        return;
      }
      // Each file is checked against its own File Type (a type change can make a file that was
      // fine, such as an .md CV, unstorable), and a row without a type is refused (a lone file
      // too).
      setMissingTypeChecked(new Set(stagedFiles.map(({ file }) => fileKey(file))));
      if (stagedFiles.some((row) => rowIssue(row, true) !== undefined)) {
        return;
      }
      // [MCV-1810] Every file goes with its own File Type (every row has one, or rowIssue above
      // refused the Save). `combining` is computed from the rows' CURRENT types and the box, so a
      // row switched away from Packet is no longer merged. (A change to the files unchecks the
      // box, in applySelection.)
      // [MCV-1848] And with its own File Category, or none (a combined packet never has one).
      const files = stagedFiles.map((row) => ({
        file: row.file,
        docType: (combining ? PACKET_TYPE : row.docType)?.value as string,
        docCategory: rowCategory(row),
      }));
      const adjusted: DocumentInfoValues = {
        ...values,
        docType: combining ? PACKET_TYPE : (stagedFiles[0].docType ?? values.docType),
        // No shared File Category any more: a file without its own is filed under none.
        docCategory: { label: '', value: '' },
        docName: combining
          ? withPdfExtension(values.docName)
          : preserveExtension(values.docName, originalNameRef.current),
      };
      actor.send({
        type: 'ON_SUBMIT_ADD_FORM',
        payload: { values: adjusted, files, combine: combining },
      });
      return;
    }
    if (getFilingIssue(form.uploadedFileName, values.docType?.value, values.docCategory?.value)) {
      return;
    }
    // [MCV-1306] Warn if doc type changed on an existing document: previously parsed results
    // are keyed to the original type and may be misleading. [MCV-1761] The app's confirmation
    // dialog, not the browser's native one.
    if (
      values.docType?.value &&
      originalDocTypeRef.current &&
      values.docType.value !== originalDocTypeRef.current
    ) {
      confirmationRef.send({
        type: 'ON_SHOW_CONFIRMATION',
        payload: {
          title: 'Change document type?',
          message: TYPE_CHANGE_MESSAGE,
          confirmLabel: 'Change type',
          tone: 'neutral',
          onConfirm: () => sendEditSubmit(values),
        },
      });
      return;
    }
    sendEditSubmit(values);
  };
  // The batch runs one file at a time, so say which one it is on.
  const savingLabel =
    isAdd && isUploading
      ? uploads && uploads.total > 1
        ? `Uploading ${uploads.outcomes.length + 1} of ${uploads.total}...`
        : 'Uploading...'
      : 'Save';
  const onSubmitError = (err: unknown) =>
    actor.send({ type: 'ON_SUBMIT_FORM_ERROR', payload: { err } });
  const onCancel = () => actor.send({ type: 'ON_CANCEL_FORM' });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormLayout className="bg-white text-content-primary">
          {isAdd && (
            <FormSection componentsPerLine={1}>
              <FormRow>
                {/* [MCV-1538] The whole dashed box is now the click target, not just the
                  button on the right. Implemented as a <label> over an sr-only (not
                  `hidden`) input: the label gives the entire box native click-to-open,
                  and sr-only keeps the input focusable so keyboard users can still reach
                  it (display:none would not be). Deliberately exactly ONE interactive
                  element -- a nested <button> is "interactive content", so clicking it
                  would NOT forward to the labelled input, and wiring both would open the
                  picker twice. Browse is therefore a styled <span>, mirroring Button's
                  secondary/md classes. */}
                <input
                  ref={fileInputRef}
                  id={fileInputId}
                  type="file"
                  multiple
                  accept={DOCUMENTS_PICKER_ACCEPT}
                  className="peer sr-only"
                  disabled={isUploading}
                  onClick={() => setFileTypeError(null)}
                  onChange={handleFilesPicked}
                />
                <label
                  htmlFor={fileInputId}
                  data-cy={TAGS.uploadFileButton}
                  className="group flex flex-col items-stretch gap-4 sm:flex-row sm:items-center rounded-md border-2 border-dashed border-grey-800 bg-grey-925 p-4 cursor-pointer hover:border-grey-700 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-60"
                >
                  <DocumentPlusIcon
                    data-cy={TAGS.uploadPromptIcon}
                    className="size-8 text-primary-50 shrink-0 self-start sm:self-auto"
                    aria-hidden="true"
                  />
                  <div
                    data-cy={TAGS.uploadPromptContent}
                    className="flex flex-col gap-1 flex-1 min-w-0"
                  >
                    <div className="text-sm font-semibold text-content-primary">
                      {selectedFiles.length === 0
                        ? 'Choose files to upload'
                        : selectedFiles.length === 1
                          ? 'File selected'
                          : `${selectedFiles.length} files selected`}
                    </div>
                    {/* Supported-formats text always wraps. A selected filename wraps on phones (no hover title there) and truncates from sm up. */}
                    <div
                      data-cy={TAGS.uploadPromptText}
                      className={
                        selectedFiles.length === 1
                          ? 'text-xs text-grey-300 max-sm:[overflow-wrap:anywhere] sm:truncate'
                          : 'text-xs text-grey-300'
                      }
                    >
                      {selectedFiles.length === 0
                        ? `Supported formats: ${toTypesLabel(allowedExtensions)}.`
                        : selectedFiles.length === 1
                          ? selectedFiles[0].name
                          : 'Add more files or remove them below.'}
                    </div>
                  </div>
                  <span
                    data-cy={TAGS.uploadPromptAction}
                    className="body-text-sb inline-flex w-full sm:w-auto items-center justify-center shrink-0 h-[50px] p-4 min-w-[126px] rounded-control text-primary-50 bg-primary-t-900 group-hover:bg-primary-t-800"
                  >
                    {selectedFiles.length === 0 ? 'Browse' : 'Add more'}
                  </span>
                </label>
                {/* [MCV-1765] The chosen files, numbered in the order they are uploaded (and, when
                    combined, the order of the pages). Outside the label: a button nested in it
                    would not take its own click (MCV-1538). */}
                {/* [MCV-1810] The "File Type" label for the per-file selects below, over their
                    column: the same text, asterisk and weight as the form's Select label (FormLabel).
                    [MCV-1848] And "File Category", with no asterisk, over the optional category
                    select to its right. Below `sm` the rows wrap and each select sits under its
                    file name, so the header is hidden; every select keeps its own aria-label, which
                    is why this is aria-hidden. Its spacers mirror a row's name and remove-button
                    columns. */}
                {selectedFiles.length > 0 && (
                  <div
                    aria-hidden="true"
                    data-cy={TAGS.form.fileTypeHeader}
                    className="mt-3 hidden items-center gap-x-2 pl-6 sm:flex"
                  >
                    <span className="min-w-[8rem] flex-1 basis-40" />
                    <span className="body-text-sb w-48 text-left text-content-primary">
                      File Type
                      <span className="pl-px text-content-primary">*</span>
                    </span>
                    <span
                      data-cy={TAGS.form.fileCategoryHeader}
                      className="body-text-sb w-48 text-left text-content-primary"
                    >
                      File Category
                    </span>
                    <span className="size-6 shrink-0" />
                  </div>
                )}
                {selectedFiles.length > 0 && (
                  <ol
                    data-cy={TAGS.form.selectedFiles}
                    className="mt-3 list-decimal space-y-1 pl-6 text-sm text-content-primary sm:mt-1"
                  >
                    {stagedFiles.map(({ file, docType, docCategory }, index) => {
                      const issue = rowIssues[index];
                      const notice = rowNotices[index];
                      const errorId = `${fileInputId}-row-${index}-error`;
                      const noticeId = `${fileInputId}-row-${index}-notice`;
                      return (
                        <li key={fileKey(file)}>
                          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                            <span
                              data-cy={TAGS.form.selectedFileName}
                              className="min-w-[8rem] flex-1 basis-40 max-sm:[overflow-wrap:anywhere] sm:truncate"
                              title={file.name}
                            >
                              {file.name}
                            </span>
                            {/* [MCV-1765] Each file has its own File Type, a lone file too
                                (MCV-1810). Combined into one packet they are all Packet, so the
                                select is locked. */}
                            <FileTypeSelect
                              cyTag={TAGS.form.fileRowType}
                              ariaLabel={`File Type for ${file.name}`}
                              value={docType}
                              onChange={(picked) => setRowType(file, picked)}
                              disabled={isUploading || combining}
                              invalid={issue !== undefined}
                              errorId={errorId}
                              describedById={notice ? noticeId : undefined}
                            />
                            {/* [MCV-1848] The optional File Category of the same file, right of
                                its File Type. A combined packet is just a Packet at upload time,
                                so the select shows none and is locked while combining. */}
                            <FileCategorySelect
                              cyTag={TAGS.form.fileRowCategory}
                              ariaLabel={`File Category for ${file.name}`}
                              value={combining ? null : docCategory}
                              onChange={(picked) => setRowCategory(file, picked)}
                              disabled={isUploading || combining}
                            />
                            <button
                              type="button"
                              data-cy={TAGS.form.removeFileBtn}
                              aria-label={`Remove ${file.name}`}
                              disabled={isUploading}
                              onClick={() => removeFile(file)}
                              className="shrink-0 cursor-pointer rounded-md p-1 text-grey-300 hover:text-content-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <XMarkIcon className="size-4" aria-hidden="true" />
                            </button>
                          </div>
                          {/* [MCV-1802] A row with an error has no note (rowNotices), so the error is all it shows. */}
                          <FormFeedback
                            error={issue}
                            errorId={errorId}
                            errorCyTag={TAGS.form.fileRowError}
                            recommendation={notice}
                            recommendationId={noticeId}
                            recommendationCyTag={TAGS.form.fileRowNotice}
                          />
                        </li>
                      );
                    })}
                  </ol>
                )}
                {/* [MCV-1577] The file picker's validation used to render as a bare
                    red sentence while every other error in this dialog came through
                    FormFeedback -> FieldError. Routed through the shared feedback
                    contract so the warning icon and error token match the rest of
                    the modal. `role="alert"` (which FieldError sets) already implies
                    aria-live="assertive", so the announcement is unchanged. */}
                <FormFeedback error={fileTypeError ?? undefined} />
              </FormRow>
            </FormSection>
          )}
          <FormSection componentsPerLine={1}>
            <FormRow>
              {multipleFiles && !combining ? (
                <FormHelper text="Each file is saved under its own name." />
              ) : (
                <TextInput label={'File Name'} cyTag={TAGS.form.docName} name={'docName'} />
              )}
            </FormRow>
          </FormSection>
          {/* [MCV-1810] Adding: each file's File Type is on its row, [MCV-1848] and so is its File
              Category, so the form has neither field. Editing one document keeps File Type and
              File Category side by side. */}
          {!isAdd && (
            <FormSection componentsPerLine={2}>
              <FormRow>
                <Select
                  name={'docType'}
                  label={'File Type'}
                  cyTag={TAGS.form.docType}
                  options={DOCUMENT_TYPES}
                  formatOptionLabel={withParseHint(DOCUMENT_TYPE_PARSE_HINTS)}
                  disabled={isAttestation}
                  required
                  onChange={onEditTypeChange}
                  externalErrorId={filingIssue ? filingErrorId : undefined}
                />
              </FormRow>
              <FormRow>
                <Select
                  name={'docCategory'}
                  label={'File Category'}
                  cyTag={TAGS.form.docCategory}
                  options={DOCUMENT_CATEGORIES}
                  formatOptionLabel={withParseHint(DOCUMENT_CATEGORY_PARSE_HINTS)}
                  disabled={isAttestation}
                  // A category picked by hand is the user's, whatever was defaulted before.
                  onChange={() => {
                    categoryAutoDefaulted.current = false;
                  }}
                  externalErrorId={filingIssue ? filingErrorId : undefined}
                />
              </FormRow>
            </FormSection>
          )}
          {filingIssue && (
            <FormSection componentsPerLine={1}>
              <FormRow>
                <FormFeedback
                  error={filingIssue}
                  errorId={filingErrorId}
                  errorCyTag={TAGS.form.fileTypeFormatError}
                />
              </FormRow>
            </FormSection>
          )}
          {offerCombine && (
            <FormSection componentsPerLine={1}>
              <FormRow>
                <Checkbox
                  name="combineFiles"
                  label="Combine into one packet"
                  cyTag={TAGS.form.combineFiles}
                  description={
                    combineBlocker ??
                    'Merge the files, in the order listed, into one PDF that is parsed as a single packet.'
                  }
                  disabled={combineBlocker !== null}
                  rowReverse
                />
              </FormRow>
            </FormSection>
          )}
          <FormSection componentsPerLine={1}>
            <FormRow>
              <TextArea
                name={'docDesc'}
                rows={4}
                label={'File Description'}
                cyTag={TAGS.form.docDesc}
              />
            </FormRow>
          </FormSection>
          <FormActions>
            <Button
              cyTag={TAGS.form.cancelBtn}
              onClick={onCancel}
              type="button"
              label="Cancel"
              colorVariant="secondary"
            />
            <Button
              cyTag={TAGS.form.submitBtn}
              type="submit"
              label={savingLabel}
              disabled={(isAdd && selectedFiles.length === 0) || isUploading}
            >
              {isUploading && (
                <span
                  aria-hidden="true"
                  className="mr-2 inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent align-middle"
                />
              )}
            </Button>
          </FormActions>
        </FormLayout>
      </form>
    </FormProvider>
  );
};
